import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { listTeamRubrics, updateTeamRubric } from 'js/actions/rubric-actions';
import { useQuery } from 'js/hooks/useQuery';
import TeamRubricsComponent from '../components/TeamRubricsComponent/TeamRubricsComponent';

function TeamRubricsContainer({
  team = null,
  teamConnectorConfigurations = [],
  teamConnectorConfigurationsHasLoaded = false,
  listTeamRubrics,
  updateTeamRubric,
}) {
  const { workspaceId, teamId } = useParams();
  const queryId = useMemo(
    () => `team-rubrics/${workspaceId}/${teamId}`,
    [workspaceId, teamId]
  );
  const [updatingIds, setUpdatingIds] = useState({});
  const queryError = useSelector((state) => state.queries[queryId]?.error);

  const loadQuery = useCallback(
    (qid) => listTeamRubrics(teamId, qid),
    [listTeamRubrics, teamId]
  );

  const { refresh, result, isLoading, hasLoaded } = useQuery({
    queryId,
    query: loadQuery,
  });

  useEffect(() => {
    refresh();
  }, [refresh]);

  const rows = useMemo(() => {
    const list = Array.isArray(result) ? result : [];
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [result]);

  const setUpdating = (rubricId, updating) => {
    setUpdatingIds((prev) => {
      const next = { ...prev };
      if (updating) {
        next[rubricId] = true;
      } else {
        delete next[rubricId];
      }
      return next;
    });
  };

  const handleToggleFavorite = async (row) => {
    const rubricId = row.id;
    setUpdating(rubricId, true);
    try {
      await updateTeamRubric(teamId, rubricId, {
        is_favorite: !row.favorite,
      });
      await refresh();
    } catch (_error) {
      // list unchanged on failure
    } finally {
      setUpdating(rubricId, false);
    }
  };

  const { favorites, suggested, allRubrics } = useMemo(() => {
    const favoriteRows = rows.filter((r) => r.favorite);
    const suggestedRows = rows.filter((r) => r.suggested);
    return {
      favorites: favoriteRows,
      suggested: suggestedRows,
      allRubrics: rows,
    };
  }, [rows]);

  return (
    <TeamRubricsComponent
      workspaceId={workspaceId}
      teamId={teamId}
      team={team}
      teamConnectorConfigurations={teamConnectorConfigurations}
      teamConnectorConfigurationsHasLoaded={
        teamConnectorConfigurationsHasLoaded
      }
      loaded={hasLoaded && !isLoading}
      error={queryError}
      favorites={favorites}
      suggested={suggested}
      allRubrics={allRubrics}
      onToggleFavorite={handleToggleFavorite}
      isFavoriteUpdating={(rubricId) => !!updatingIds[rubricId]}
    />
  );
}

export default connect((state) => ({}), {
  listTeamRubrics,
  updateTeamRubric,
})(TeamRubricsContainer);
